import React, { useState, useEffect } from "react";
import { CircularProgress } from "@mui/material";
import { useExportAppSessionsMutation } from "../../../features/tenant/ActiveUsers/activeUsersApi"

// ─── export report modal ──────────────────────────────────────────────────────

const ExportReportModal = ({ onClose, prefillUserId = "" }) => {
  const token     = sessionStorage.getItem("access_token");
  const tenant_id = JSON.parse(sessionStorage.getItem("vendorData"))?.tenant_id;

  const [exportUserId,  setExportUserId]  = useState(prefillUserId);
  const [startDate,     setStartDate]     = useState("");
  const [endDate,       setEndDate]       = useState("");
  const [exportStatus,  setExportStatus]  = useState("idle"); // idle | loading | success | error
  const [errorMsg,      setErrorMsg]      = useState("");
  const [fileInfo,      setFileInfo]      = useState(null);  // { fileLink, fileName }

  const [exportAppSessions] = useExportAppSessionsMutation();

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const handleGenerate = async () => {
    setExportStatus("loading");
    setErrorMsg("");
    setFileInfo(null);
    try {
      const res = await exportAppSessions({
        token, tenant_id,
        user_id:    exportUserId  || undefined,
        start_date: startDate     || undefined,
        end_date:   endDate       || undefined,
      });

      if (res?.data?.success) {
        const { fileLink, fileName } = res.data.body;
        setFileInfo({ fileLink, fileName });
        setExportStatus("success");

        // auto-trigger download
        const a = document.createElement("a");
        a.href = fileLink;
        a.setAttribute("download", fileName);
        a.target = "_blank";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        setExportStatus("error");
        setErrorMsg(res?.error?.data?.message || "Failed to generate report.");
      }
    } catch (err) {
      console.error("Export error", err);
      setExportStatus("error");
      setErrorMsg("Something went wrong. Please try again.");
    }
  };

  const handleReset = () => {
    setExportUserId(prefillUserId);
    setStartDate(""); setEndDate("");
    setExportStatus("idle");
    setErrorMsg(""); setFileInfo(null);
  };

  const isLoading = exportStatus === "loading";

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 1100,
        background: "rgba(15,23,42,0.6)",
        backdropFilter: "blur(5px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 20,
          width: "100%", maxWidth: 480,
          boxShadow: "0 25px 60px rgba(0,0,0,0.25)",
          overflow: "hidden",
          fontFamily: "'Plus Jakarta Sans','Segoe UI',sans-serif",
        }}
      >
        {/* ── header ── */}
        <div style={{
          background: "linear-gradient(135deg, #1e3a5f 0%, #1d4ed8 55%, #3b82f6 100%)",
          padding: "22px 24px",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 42, height: 42, borderRadius: 12,
              background: "rgba(255,255,255,0.18)",
              border: "1.5px solid rgba(255,255,255,0.3)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10 9 9 9 8 9"/>
              </svg>
            </div>
            <div>
              <div style={{ color: "#fff", fontWeight: 800, fontSize: 16 }}>Export Sessions Report</div>
              <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 12, marginTop: 2 }}>
                Downloads as an Excel file (.xlsx)
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 32, height: 32, borderRadius: 8,
              background: "rgba(255,255,255,0.15)",
              border: "1px solid rgba(255,255,255,0.25)",
              color: "#fff", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 20, lineHeight: 1, flexShrink: 0,
            }}
          >×</button>
        </div>

        {/* ── body ── */}
        <div style={{ padding: "24px 24px 20px" }}>

          {/* info note */}
          <div style={{
            background: "#eff6ff", border: "1px solid #bfdbfe",
            borderRadius: 10, padding: "10px 14px",
            display: "flex", gap: 10, alignItems: "flex-start",
            marginBottom: 20,
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <span style={{ fontSize: 12, color: "#1e40af", lineHeight: 1.5 }}>
              All filters are optional. Leave them empty to export the complete sessions report.
            </span>
          </div>

          {/* filter fields */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

            {/* user id */}
            <div>
              <label style={labelStyle}>
                User ID
                <span style={{ color: "#9ca3af", fontWeight: 400, marginLeft: 4 }}>(optional)</span>
              </label>
              <div style={{ position: "relative" }}>
                <span style={iconStyle}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                  </svg>
                </span>
                <input
                  type="text"
                  placeholder="e.g. DLR123456"
                  value={exportUserId}
                  onChange={e => setExportUserId(e.target.value)}
                  disabled={isLoading}
                  style={{ ...inputStyle, opacity: isLoading ? 0.6 : 1 }}
                />
              </div>
            </div>

            {/* date row */}
            <div style={{ display: "flex", gap: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>
                  Start Date
                  <span style={{ color: "#9ca3af", fontWeight: 400, marginLeft: 4 }}>(optional)</span>
                </label>
                <div style={{ position: "relative" }}>
                  <span style={iconStyle}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                    </svg>
                  </span>
                  <input
                    type="date"
                    value={startDate}
                    onChange={e => setStartDate(e.target.value)}
                    disabled={isLoading}
                    style={{ ...inputStyle, opacity: isLoading ? 0.6 : 1 }}
                  />
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>
                  End Date
                  <span style={{ color: "#9ca3af", fontWeight: 400, marginLeft: 4 }}>(optional)</span>
                </label>
                <div style={{ position: "relative" }}>
                  <span style={iconStyle}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                    </svg>
                  </span>
                  <input
                    type="date"
                    value={endDate}
                    onChange={e => setEndDate(e.target.value)}
                    disabled={isLoading}
                    style={{ ...inputStyle, opacity: isLoading ? 0.6 : 1 }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ── status feedback ── */}

          {/* error */}
          {exportStatus === "error" && (
            <div style={{
              marginTop: 16,
              background: "#fef2f2", border: "1px solid #fecaca",
              borderRadius: 10, padding: "12px 14px",
              display: "flex", gap: 10, alignItems: "center",
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
              <span style={{ fontSize: 13, color: "#991b1b" }}>{errorMsg}</span>
            </div>
          )}

          {/* success */}
          {exportStatus === "success" && fileInfo && (
            <div style={{
              marginTop: 16,
              background: "#f0fdf4", border: "1px solid #bbf7d0",
              borderRadius: 10, padding: "14px 16px",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <div style={{
                  width: 28, height: 28, borderRadius: "50%",
                  background: "linear-gradient(135deg,#059669,#047857)",
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </div>
                <span style={{ fontWeight: 700, fontSize: 13, color: "#065f46" }}>
                  Report generated! Download started automatically.
                </span>
              </div>

              {/* file chip + manual download */}
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                background: "#fff", borderRadius: 8, padding: "8px 12px",
                border: "1px solid #d1fae5",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                  </svg>
                  <span style={{
                    fontSize: 12, color: "#065f46", fontFamily: "'DM Mono',monospace",
                    fontWeight: 600, maxWidth: 240,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>
                    {fileInfo.fileName}
                  </span>
                </div>
                <a
                  href={fileInfo.fileLink}
                  download={fileInfo.fileName}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 5,
                    background: "linear-gradient(135deg,#059669,#047857)",
                    color: "#fff", borderRadius: 7, padding: "5px 12px",
                    fontSize: 12, fontWeight: 700, textDecoration: "none",
                    boxShadow: "0 1px 4px rgba(5,150,105,0.3)",
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                  Download
                </a>
              </div>
            </div>
          )}

          {/* ── action buttons ── */}
          <div style={{ display: "flex", gap: 10, marginTop: 22 }}>
            <button
              onClick={handleReset}
              disabled={isLoading}
              style={{
                flex: "0 0 auto",
                display: "flex", alignItems: "center", gap: 6,
                background: "#fff", color: "#6b7280",
                border: "1.5px solid #e5e7eb", borderRadius: 10,
                padding: "10px 18px", fontSize: 13, fontWeight: 600,
                fontFamily: "'Plus Jakarta Sans',sans-serif",
                cursor: isLoading ? "not-allowed" : "pointer",
                opacity: isLoading ? 0.5 : 1,
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-5.49"/>
              </svg>
              Reset
            </button>

            <button
              onClick={handleGenerate}
              disabled={isLoading}
              style={{
                flex: 1,
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                background: isLoading
                  ? "#e5e7eb"
                  : "linear-gradient(135deg,#1d4ed8,#2563eb)",
                color: isLoading ? "#9ca3af" : "#fff",
                border: "none", borderRadius: 10,
                padding: "11px 20px", fontSize: 14, fontWeight: 700,
                fontFamily: "'Plus Jakarta Sans',sans-serif",
                cursor: isLoading ? "not-allowed" : "pointer",
                boxShadow: isLoading ? "none" : "0 3px 12px rgba(37,99,235,0.35)",
                transition: "all 0.2s",
              }}
            >
              {isLoading ? (
                <>
                  <CircularProgress size={15} style={{ color: "#9ca3af" }} />
                  Generating Report…
                </>
              ) : exportStatus === "success" ? (
                <>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  Generate Again
                </>
              ) : (
                <>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                  Generate & Download Report
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── styles ───────────────────────────────────────────────────────────────────

const labelStyle = {
  display: "block", fontSize: 12, fontWeight: 600, color: "#374151",
  marginBottom: 5, fontFamily: "'Plus Jakarta Sans',sans-serif", letterSpacing: 0.3,
};
const iconStyle = {
  position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)",
  color: "#9ca3af", pointerEvents: "none", display: "flex",
};
const inputStyle = {
  width: "100%", paddingLeft: 34, paddingRight: 10, paddingTop: 9, paddingBottom: 9,
  border: "1.5px solid #e5e7eb", borderRadius: 9, fontSize: 13, color: "#111827",
  fontFamily: "'Plus Jakarta Sans',sans-serif", outline: "none",
  background: "#fafafa", boxSizing: "border-box", transition: "border-color 0.2s",
};

export default ExportReportModal;
